import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { SkillsService } from '../../../shared/services/skills-service';
import EmployeeProfile from './EmployeeProfile';
import './EmployeeProfile.scss';
import { EmployeesService } from '../../../shared/services/employees';
import LayoutContainer from '../../../shared/components/_layout/layout-container/LayoutContainer';
import TitleBarContainer from '../../../shared/components/top-bars/title-bar/TitleBarContainer';
import { CertificatesService } from 'modules/shared/services/certificates-service';

const EmployeeProfileContainer = props => {
  const [employee, setEmployee] = useState({});
  const [supervisorData, setSupervisorData] = useState({});
  const [assignedProjects, setAssignedProjects] = useState(undefined);
  const [skills, setSkills] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [canEditSkills, setCanEditSkills] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [successToastText, setSuccessToastText] = useState('');
  const [errorToastText, setErrorToastText] = useState('');
  const [toDelete, setToDelete] = useState(undefined);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const savedPathname = localStorage.getItem('pathname');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [certificateData, setCertificateData] = useState({
    name: '',
    url: '',
    certificationDate: ''
  });
  const isAdmin = () => {
    const { userPermissions: permissions } = props;
    return permissions && Array.isArray(permissions) && permissions.includes('admin');
  };
  const navigate = route => {
    props.history.push(route);
  };
  useEffect(() => {
    const empId = +props.match.params.id;
    loadData(empId);
    fetchEmployeeCertificates(empId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.match.params.id]);
  const loadSupervisorData = async data => {
    try {
      const { data: employee } = await EmployeesService.getEmpByID(data?.id);
      setSupervisorData(employee);
    } catch {
      // Supervisor may be outside the viewer's access scope
    }
  };
  const openCertificatesDialog = certificate => {
    setIsDialogOpen(true);
    setCertificateData(certificate);
  };
  const closeCertificatesDialog = () => {
    setIsDialogOpen(false);
    setCertificateData(undefined);
  };
  const closeSuccessToast = () => {
    setShowSuccessToast(false);
    setSuccessToastText('');
  };
  const closeErrorToast = () => {
    setShowErrorToast(false);
    setErrorToastText('');
  };
  const getSkillLevel = s => {
    const a = [];
    for (let i = 0; i < s; i++) a.push(1);
    return a;
  };
  const onCertificateCreate = (state, errorMessage) => {
    if (state === 'success') {
      setShowSuccessToast(true);
      setSuccessToastText('Certificate saved successfully');
      closeCertificatesDialog();
      fetchEmployeeCertificates(employee?.id);
    } else {
      setShowErrorToast(true);
      setErrorToastText(errorMessage);
    }
  };
  const fetchEmployeeCertificates = async id => {
    const { data: certificates } = await CertificatesService.getEmployeeCertificates(id);
    setCertificates(certificates);
  };
  const confirmDelete = certificate => {
    setToDelete({ id: certificate.id, name: certificate.name, type: 'Certificate' });
    setIsDeleteDialogOpen(true);
  };
  const closeDeleteDialog = async data => {
    const certificateId = data?.id;
    if (certificateId) {
      try {
        await CertificatesService.deleteEmployeeCertificates(employee?.id, certificateId);
        setShowSuccessToast(true);
        setSuccessToastText('Certificate removed successfully!');
        fetchEmployeeCertificates(employee?.id);
        setToDelete(undefined);
        setIsDeleteDialogOpen(false);
      } catch {
        setShowErrorToast(true);
        setErrorToastText('Cannot remove Certificate');
        setToDelete(undefined);
        setIsDeleteDialogOpen(false);
      }
    }
    setIsDeleteDialogOpen(false);
  };

  const loadData = async empId => {
    try {
      setIsLoading(true);
      let { data: employee } = await EmployeesService.getEmpByID(empId);
      if (!employee || employee === null) {
        employee = {};
      }
      const { data: lead } = await EmployeesService.getProjectsAsLead(empId);
      const { data: member } = await EmployeesService.getProjectsAsMember(empId);
      const assignedProjects = { lead, member };
      const empSkills = await EmployeesService.getSkills(empId);
      const skills = await SkillsService.filter();
      let leveledSkills = skills
        .map(skill => ({
          ...skill,
          level: (empSkills.find(empSkill => empSkill.id === skill.id) || { level: 0 }).level,
          usedInAspire: empSkills.find(empSkill => empSkill.id === skill.id)?.usedInAspire,
          experienceId: empSkills.find(empSkill => empSkill.id === skill.id)?.experienceId,
          lastAssessment: empSkills.find(empSkill => empSkill.id === skill.id)?.lastAssessment
        }))
        .filter(skill => skill.level && skill.level > 0);
      const groupedSkillsObject = _.groupBy(leveledSkills, skill => {
        return skill.group;
      });
      const groupedSkillsArray = [];
      for (const [key, value] of Object.entries(groupedSkillsObject)) {
        groupedSkillsArray.push({ groupName: key, skills: value });
      }
      const sortedGroupedSkills = groupedSkillsArray?.sort(
        (a, b) => b?.skills?.length - a?.skills?.length
      );

      let canEditSkills = false;
      if (+props.currentUser.id === +empId || isAdmin()) {
        canEditSkills = true;
      }
      setEmployee(employee);
      employee?.reportingTo?.id && loadSupervisorData(employee?.reportingTo);
      setAssignedProjects(assignedProjects);
      setSkills(sortedGroupedSkills);
      setCanEditSkills(canEditSkills);
      setIsLoading(false);
    } catch (error) {
      if (error?.response?.status) {
        props.history.push('/accessdenied');
      }
    }
  };
  const titleBar = (
    <TitleBarContainer
      title={employee?.displayName || 'Employee Profile'}
      backRoute={isAdmin() ? savedPathname : '/'}
    />
  );
  return (
    <LayoutContainer titleBar={titleBar}>
      <EmployeeProfile
        employee={employee}
        supervisorData={supervisorData}
        canEditSkills={canEditSkills}
        skillsGroups={skills}
        getSkillLevel={getSkillLevel}
        assignedProjects={assignedProjects}
        navigate={navigate}
        isLoading={isLoading}
        isAdmin={isAdmin}
        certificates={certificates}
        isDialogOpen={isDialogOpen}
        closeCertificatesDialog={closeCertificatesDialog}
        openCertificatesDialog={openCertificatesDialog}
        certificateData={certificateData}
        setCertificateData={setCertificateData}
        setCertificates={setCertificates}
        onCertificateCreate={onCertificateCreate}
        errorToastText={errorToastText}
        successToastText={successToastText}
        showErrorToast={showErrorToast}
        showSuccessToast={showSuccessToast}
        closeErrorToast={closeErrorToast}
        closeSuccessToast={closeSuccessToast}
        confirmDelete={confirmDelete}
        toDelete={toDelete}
        isDeleteDialogOpen={isDeleteDialogOpen}
        closeDeleteDialog={closeDeleteDialog}
      />
    </LayoutContainer>
  );
};

const mapStateToProps = store => ({
  currentUser: store.auth.currentUser,
  userPermissions: store.auth.userPermissions
});

export default connect(mapStateToProps, null)(EmployeeProfileContainer);
